import React, { useEffect, useState } from 'react'
import { Box } from '@mui/material'
import {
  Button,
  Card,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  FormControlLabel,
  Grid,
  IconButton,
  makeStyles,
  Radio,
  RadioGroup,
  Tooltip,
  Typography
} from '@material-ui/core'
import useTokenValidation from 'hooks/useTokenValidation'
import PageNotFound from 'components/PageNotFound'
import { ArrowBack, Delete, Edit, Warning } from '@material-ui/icons'
import { useHistory, useParams } from 'react-router-dom'
import { Form, Formik } from 'formik'
import CustomTextField from 'components/Form/CustomTextField'
import { useDispatch, useSelector } from 'react-redux'
import ProgressButton from 'components/ProgressButton'
import PollingForm from 'views/admin/subjects/PollingForm/PollingForm'
import { nanoid } from 'nanoid'
import { speakerTalkSchema } from 'schemas/subjects'
import talkSlice, { addTalk, fetchTalkById, updateTalkById } from 'store/talk'
import pollingSlice, {
  addPollAlongWithQuestions,
  deletePollById,
  fetchPollAlongQuestions,
  pollAttachOrDetach,
  updatePollAlongWithQuestions
} from 'store/polling'
import { toast } from 'react-toastify'
import { isEqual } from 'lodash'
import ConfirmModal from 'components/ConfirmationModal'
import Recaptcha from 'components/Recaptcha'
import useRecaptcha from 'hooks/useCustomRecaptcha'
import { SubjectStatus, TalkStatus } from 'constants/index'
import { fetchSubjectById, updateSubjectStatus, verifyGoogleRecaptcha } from 'store/subject'
import { returnTimeInTimezone } from 'utils'
import { getDefaultTimezone } from 'utils/times'
import { colors } from 'utils/colors'
import LogoWithConference from 'components/LogoWithConference'

const emptyQuestion = {
  title: '',
  options: [
    { optId: nanoid(), opt: '' },
    { optId: nanoid(), opt: '' }
  ]
}
const pollInitialValues = {
  title: '',
  questions: [emptyQuestion]
}
const SubjectTalkForm = ({ forApproval }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const classes = useStyles()
  const { subjectId, talkId } = useParams()
  const { isLoading, isTokenValid } = useTokenValidation()
  const { isRecaptchaValid, recaptchaCode, handleChangeRecaptcha, handleExpired } = useRecaptcha()
  const [remountRecaptcha, setRemountRecaptcha] = useState(false)
  const [loading, setLoading] = useState(false)
  const [pollFetched, setPollFetched] = useState(false)
  const [pollValues, setPollValues] = useState(pollInitialValues)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [openConfirmModal, setOpenConfirmModal] = useState(false)
  const { subject } = useSelector((state) => state.subjects)
  const { conference } = useSelector((state) => state.conference)
  const { talk, inProgress } = useSelector((state) => state.talk)
  const { poll } = useSelector((state) => state.polling)
  const defaultTimezone = getDefaultTimezone(conference?.timezone)

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false)
  }
  const handleConfirm = () => {
    setPollValues(pollInitialValues)
  }

  useEffect(() => {
    if (talkId) {
      dispatch(fetchTalkById(talkId))
    }
    return () => dispatch(talkSlice.actions.setTalk(null))
  }, [talkId])

  useEffect(() => {
    if (subjectId && forApproval) {
      dispatch(fetchSubjectById(subjectId))
    }
  }, [subjectId, forApproval])

  useEffect(() => {
    if (talkId && talk?.havePolls && talk?.pollId) {
      setPollFetched(true)
      fetchPollAlongQuestions(talk?.pollId).then((data) => {
        dispatch(pollingSlice.actions.setPoll(data))
        setPollFetched(false)
      })
    }
    return () => dispatch(pollingSlice.actions.setPoll(null))
  }, [talkId, talk?.pollId])

  useEffect(() => {
    if (poll?.id) {
      setPollValues(poll)
    }
  }, [poll?.id])

  const handleCloseModal = (formValues) => {
    if (isEqual(pollValues, formValues)) {
      setIsModalOpen(false)
    } else {
      setOpenConfirmModal(true)
    }
  }

  const handleConfirmWarning = () => {
    setOpenConfirmModal(false)
    setIsModalOpen(false)
  }
  const handleOpenModal = () => {
    setIsModalOpen(true)
  }

  const initialValues = {
    title: talk?.title ?? '',
    synopsis: talk?.synopsis ?? '',
    havePolls: talk?.havePolls ? 'yes' : 'no',
    eventType: talk?.eventType ?? '',
    eventId: talk?.eventId ?? '',
    sessionId: talk?.sessionId ?? '',
    venueId: talk?.venueId ?? '',
    startDate: talk?.startDate
      ? returnTimeInTimezone(talk?.startDate, conference?.timezone, defaultTimezone)
      : '',
    endDate: talk?.endDate
      ? returnTimeInTimezone(talk?.endDate, conference?.timezone, defaultTimezone)
      : '',
    sponsorId: talk?.sponsorId ?? ''
  }

  const customSubmitPoll = (values) => {
    setPollValues(values)
    setIsModalOpen(false)
  }

  const goBack = () => history.goBack()

  const onSubmit = async (values) => {
    setLoading(true)
    if (!forApproval && !(await dispatch(verifyGoogleRecaptcha(recaptchaCode)))) {
      setLoading(false)
      toast.error('reCAPTCHA verification is failed.')
      return
    }

    const payload = {
      title: values.title.trim(),
      synopsis: values.synopsis.trim(),
      havePolls: values.havePolls === 'yes',
      subjectId: subject.id,
      status:
        talkId && talk?.status === TalkStatus.ASSOCIATED
          ? TalkStatus.ASSOCIATED
          : TalkStatus.NOT_ASSOCIATED,
      conferenceId: subject?.conferenceId
    }
    if (payload.havePolls && isEqual(pollValues, pollInitialValues)) {
      toast.warning('Please create poll to associate with talk.')
      setRemountRecaptcha(true)
      setLoading(false)
      return
    }

    let message = 'Talk updated successfully.'
    if (!talkId) {
      const talkId = await dispatch(addTalk(payload))
      if (payload?.havePolls && talkId) {
        const updatedValues = {
          ...pollValues,
          talkId,
          eventId: payload.eventId,
          subjectId: subject.id,
          conferenceId: subject?.conferenceId
        }
        const pollId = await dispatch(addPollAlongWithQuestions(updatedValues))
        await dispatch(updateTalkById(talkId, { pollId }))
      }
      message = 'Talk created successfully.'
    } else if (payload?.havePolls && !poll?.id) {
      const updatedValues = {
        ...pollValues,
        talkId,
        eventId: payload.eventId,
        subjectId: subject.id,
        conferenceId: subject?.conferenceId
      }
      const pollId = await dispatch(addPollAlongWithQuestions(updatedValues))
      payload.havePolls = true
      payload.pollId = pollId
      await dispatch(updateTalkById(talkId, payload))
    } else if (payload?.havePolls && poll?.id) {
      payload.pollId = payload?.havePolls ? pollValues.id : ''
      await dispatch(updateTalkById(talkId, payload))
      if (!isEqual(poll, pollValues)) {
        const missingQuestionIds = poll?.questions
          .filter(
            (initialQuestion) =>
              !pollValues.questions.some(
                (updatedQuestion) => updatedQuestion.id === initialQuestion.id
              )
          )
          .map((question) => question.id)
        await dispatch(updatePollAlongWithQuestions(poll?.id, pollValues, missingQuestionIds))
      }
    } else if (!payload?.havePolls && poll?.id) {
      payload.pollId = payload?.havePolls ? pollValues.id : ''
      await dispatch(updateTalkById(talkId, payload))
      await dispatch(deletePollById(poll?.id, { validateChecks: false }))
    } else {
      await dispatch(updateTalkById(talkId, payload))
    }
    !forApproval &&
      (!talkId || (talkId && talk?.status === TalkStatus.NOT_ASSOCIATED)) &&
      (await dispatch(updateSubjectStatus(payload?.subjectId, SubjectStatus.PENDING_DATA_REVIEW)))
    if (forApproval && payload?.havePolls && payload?.eventId) {
      await pollAttachOrDetach(payload.pollId, { eventId: payload.eventId })
    }

    toast.success(message)
    setLoading(false)
    goBack()
  }

  if (isLoading || inProgress || pollFetched) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: '5rem' }}>
        <CircularProgress />
      </Box>
    )
  } else if (!forApproval && !isTokenValid) {
    return <PageNotFound />
  }
  return (
    <Container className={classes.container}>
      {!forApproval && <LogoWithConference confName={conference?.confName} />}
      <Card className={classes.card} elevation={0}>
        <Box className={classes.header}>
          <Box className={classes.heading}>
            <IconButton onClick={goBack} className={classes.iconBtn}>
              <ArrowBack />
            </IconButton>
            <Typography variant="h5">{talkId ? 'Edit Talk' : 'New Talk'}</Typography>
          </Box>
        </Box>

        <Formik
          initialValues={initialValues}
          enableReinitialize={true}
          onSubmit={onSubmit}
          validationSchema={speakerTalkSchema}>
          {({ values, setFieldValue }) => (
            <Form className={classes.form}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Card elevation={0}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} lg={8} style={{ marginTop: '8px' }}>
                        <CustomTextField label="Talk Title" name="title" type="text" />
                      </Grid>

                      <Grid item xs={12} lg={12}>
                        <CustomTextField
                          label="Synopsis"
                          name="synopsis"
                          type="text"
                          multiline
                          minRows={5}
                          maxRows={30}
                        />
                      </Grid>

                      {conference?.havePolls && (
                        <Grid item xs={12} md={8}>
                          <Box className={classes.talkSection}>
                            {conference?.havePolls && (
                              <Box className={classes.radioGroup}>
                                <Typography className={classes.radioLabel}>
                                  Do you want to associate a poll with this talk?
                                </Typography>
                                <RadioGroup
                                  row
                                  value={values.havePolls}
                                  onChange={(e) => setFieldValue('havePolls', e.target.value)}>
                                  <FormControlLabel
                                    control={<Radio size="small" />}
                                    label={<Typography variant="body2">Yes</Typography>}
                                    value="yes"
                                  />
                                  <FormControlLabel
                                    control={<Radio size="small" />}
                                    label={<Typography variant="body2">No</Typography>}
                                    value="no"
                                  />
                                </RadioGroup>

                                {values.havePolls === 'yes' && !pollValues?.title && (
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    onClick={handleOpenModal}>
                                    Create Poll
                                  </Button>
                                )}
                              </Box>
                            )}

                            {values.havePolls === 'yes' && pollValues?.title && (
                              <Card className={classes.pollCard}>
                                <Typography>{pollValues?.title}</Typography>
                                <Box>
                                  <Tooltip title="Edit">
                                    <IconButton
                                      className={classes.iconButton}
                                      onClick={handleOpenModal}>
                                      <Edit />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete">
                                    <IconButton
                                      className={classes.iconButton}
                                      onClick={() => setIsDeleteModalOpen(true)}>
                                      <Delete />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Card>
                            )}
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </Card>
                </Grid>
              </Grid>

              <Dialog fullWidth maxWidth="xl" open={isModalOpen}>
                <DialogContent className={classes.pollModalContent}>
                  <PollingForm
                    forApproval={false}
                    editedPollId={pollValues?.title}
                    pollValues={pollValues}
                    speakerId={null}
                    conferenceId={subject?.conferenceId}
                    subjectId={subject?.id}
                    handleCloseModal={handleCloseModal}
                    handleCloseConfirmModal={() => {}}
                    isCustomSubmit={true}
                    handleCustomSubmit={customSubmitPoll}
                  />
                </DialogContent>
              </Dialog>

              <ConfirmModal
                dialogTitle="Delete Poll"
                dialogText="Do you really want to delete this poll? This will also remove all questions within this poll."
                open={isDeleteModalOpen}
                closeModal={handleCloseDeleteModal}
                handleConfirm={handleConfirm}
                inProgress={loading}
              />

              <Dialog
                fullWidth
                maxWidth="sm"
                open={openConfirmModal}
                onClose={() => setOpenConfirmModal(false)}>
                <Box className={classes.warningModalHeader}>
                  <Warning className={classes.warningIcon} />
                  <Typography className={classes.warningTitle}>Warning</Typography>
                </Box>
                <DialogContent className={classes.modalContent}>
                  <Typography variant="body2">
                    Any unsaved data will be lost. Press CANCEL to go back and save these changes,
                    or OK to discard these changes.
                  </Typography>

                  <Box className={classes.actions}>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      onClick={handleConfirmWarning}>
                      ok
                    </Button>
                    <Button size="medium" onClick={() => setOpenConfirmModal(false)}>
                      cancel
                    </Button>
                  </Box>
                </DialogContent>
              </Dialog>
              <Grid container spacing={2} className={classes.actionButtons}>
                <Box className={classes.buttonBox}>
                  {!forApproval && (
                    <Recaptcha
                      handleChange={handleChangeRecaptcha}
                      onExpired={handleExpired}
                      remount={remountRecaptcha}
                    />
                  )}
                  <Box sx={{ display: 'flex', gap: '1rem' }}>
                    <Button size="medium" disabled={loading} onClick={goBack}>
                      Cancel
                    </Button>
                    <ProgressButton
                      disabled={loading || (!forApproval && !isRecaptchaValid)}
                      inProgress={loading}
                      type="submit"
                      size="large"
                      label={talkId ? 'Update Talk' : 'Create Talk'}
                    />
                  </Box>
                </Box>
              </Grid>
            </Form>
          )}
        </Formik>
      </Card>
    </Container>
  )
}

export default SubjectTalkForm

const useStyles = makeStyles({
  customCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.75rem',
    padding: '1rem',
    border: '1px solid #9e9e9e'
  },
  warningModalHeader: {
    padding: '20px 20px 10px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  warningTitle: {
    fontSize: '16px',
    fontWeight: 'bold'
  },
  warningIcon: {
    color: '#ffa726'
  },
  actions: {
    marginTop: '20px',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    marginBottom: '10px'
  },
  container: {
    paddingBottom: '1.5rem',
    paddingTop: '1.5rem'
  },
  card: {
    padding: '1rem',
    border: '1px solid #e0e0e0'
  },
  form: {
    marginTop: '32px'
  },
  iconBtn: {
    padding: '8px'
  },
  heading: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  radioGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem'
  },
  radioLabel: {
    fontWeight: 600,
    fontSize: '16px',
    color: '#2f2f2f'
  },
  actionButtons: {
    marginTop: '16px',
    display: 'flex',
    justifyContent: 'end'
  },
  pollModalContent: {
    paddingTop: '0px !important'
  },
  pollCard: {
    padding: '10px 30px',
    boxShadow: 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  iconButton: {
    padding: '8px'
  },
  talkSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    border: '1px solid #bdbdbd',
    borderRadius: '10px',
    padding: '16px'
  },
  buttonBox: {
    marginTop: '1rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '1rem',
    padding: '6px'
  },
  cardsInfo: {
    color: colors.indigo,
    fontSize: '15px',
    fontWeight: 600
  },
  textField: {
    width: '100%'
  }
})
